import * as Sentry from '@sentry/browser';
import { UAParser } from 'ua-parser-js';
import { Event, EventHint } from '@sentry/types';

interface SentryScopeData {
  userId?: string;
  widgetMode?: string;
  viewMode?: string;
  deviceType?: string;
  locale?: string;
  isBranded?: string;
}

const isBrowserSupported = () => {
  try {
    const { os, browser } = new UAParser(self.navigator.userAgent).getResult();
    const osVersion = parseFloat(os.version as string);
    const browserMajorVersion = parseInt(
      (browser.version as string).split('.')[0],
      10,
    );
    const unsupportedIOS = os.name === 'iOS' && osVersion < 11;
    const unsupportedAndroid = os.name === 'Android' && osVersion < 7;
    const unsupportedBrowser =
      (browser.name === 'QQBrowser' && browserMajorVersion < 9) ||
      (browser.name === 'Chrome' && browserMajorVersion < 50) ||
      (browser.name === 'Safari' && browserMajorVersion < 11) ||
      (browser.name === 'IE' && browserMajorVersion < 11);

    return !(unsupportedIOS || unsupportedAndroid || unsupportedBrowser);
  } catch (e) {
    return true;
  }
};

const extractClientVersion = staticsBaseUrl => {
  // extract client version from statics-base-url
  const urlParts = staticsBaseUrl
    .split('/')
    .filter(Boolean)
    .reverse();
  return urlParts.length > 1 ? urlParts[0] : undefined;
};

// This is done because we want the same error across different versions to be grouped together
const maskVersion = (clientVersion: string, event: Event, hint?: EventHint) => {
  const mask = msg => msg.replace(clientVersion, '{version}');

  if (event.exception && event.exception.values) {
    event.exception.values.forEach(ex => {
      if (ex.value) {
        const originalMessage = ex.value;
        ex.value = mask(originalMessage);
      }
      if (ex.mechanism && ex.mechanism.data && ex.mechanism.data.message) {
        const originalMessage = ex.mechanism.data.message.toString();
        ex.mechanism.data.message = mask(originalMessage);
      }
    });
  }

  if (hint && hint.data && hint.data.stack && hint.data.stack.message) {
    const originalMessage = hint.data.stack.message;
    hint.data.stack.message = mask(originalMessage);
  }
};

const mergeChunkLoadErrors = (event: Event, hint?: EventHint) => {
  const chunkLoadError = 'ChunkLoadError';
  if (
    hint &&
    hint.originalException &&
    hint.originalException instanceof Error &&
    (hint.originalException.name === chunkLoadError ||
      hint.originalException.message.indexOf('Loading CSS chunk 1 failed') !==
        -1)
  ) {
    event.fingerprint = [chunkLoadError];
  }
};

const mergeNetworkErrors = (event: Event, hint?: EventHint) => {
  const networkError =
    'A network error (such as timeout, interrupted connection or unreachable host) has occurred.';
  if (
    hint &&
    hint.originalException &&
    hint.originalException instanceof Error &&
    hint.originalException.message.indexOf(networkError) !== -1
  ) {
    event.fingerprint = ['NetworkError'];
  }
};

const enrichWithExtraDataFromException = (event: Event, hint?: EventHint) => {
  if (hint && hint.originalException) {
    const extraData = (hint.originalException as any).extraData;
    if (extraData) {
      event.extra = { ...event.extra, ...extraData };
    }
  }
};

const beforeSend = (clientVersion: string) => (
  event: Event,
  hint?: EventHint,
): Event => {
  maskVersion(clientVersion, event, hint);
  mergeChunkLoadErrors(event, hint);
  mergeNetworkErrors(event, hint);
  enrichWithExtraDataFromException(event, hint);
  return event;
};

const beforeBreadcrumb = (breadcrumb, hint) => {
  if (hint && breadcrumb.category === 'ui.click') {
    const target = hint.event.target as HTMLElement;
    const message =
      target.getAttribute('data-hook') || target.getAttribute('aria-label');
    if (message) {
      breadcrumb.message = message;
    }
  }
  return breadcrumb;
};

const configureScope = (scopeData: SentryScopeData) => {
  const {
    userId,
    widgetMode,
    viewMode,
    deviceType,
    locale,
    isBranded,
  } = scopeData;
  Sentry.configureScope(scope => {
    scope.setUser({ id: userId });

    widgetMode && scope.setTag('WidgetMode', widgetMode);
    viewMode && scope.setTag('ViewMode', viewMode);
    deviceType && scope.setTag('DeviceType', deviceType);

    locale && scope.setExtra('locale', locale);
    isBranded && scope.setExtra('IsBranded', isBranded);
  });
};

const blacklistUrls = ['unpkg/auth/dist/', 'unpkg/database/dist'];

export const initSentry = (staticsBaseUrl, scopeData: SentryScopeData) => {
  const clientVersion = extractClientVersion(staticsBaseUrl);
  if (clientVersion) {
    Sentry.onLoad(() => {
      Sentry.init({
        enabled: !!clientVersion && isBrowserSupported(),
        release: `chat-widget@${clientVersion}`,
        blacklistUrls,
        beforeBreadcrumb,
        beforeSend: beforeSend(clientVersion),
        sampleRate: 0.1,
      });
    });

    configureScope(scopeData);
  } else {
    console.warn(`Sentry wasn't initialized!`);
  }
};
